/**
 * Только что сохранённый русский эфира не откатывается на ленте к прежнему, пока сервер его не догнал.
 *
 * Проба 20261104-P50m20b, эфир 44c2e1ad, папка 4 en, E2: переводчик правил русский куска раскладки «фраза 2/2»
 * (…549793): «с пятой попытки заходить.» → «заходить приходилось с пятой попытки.». Сохранение легло в 13:00:13
 * (тост «Правка принята»), а в 13:00:20 русский у правленой строки вернулся к старому и держался до 13:00:44, когда
 * полоса правок вернула фразу целиком, перевела и разложила её заново (новые id кусков).
 *
 * Откуда старый текст. Русский куска в редакторе пары — json куска в папке пары (`subs/ru_en/synt_chunks4/
 * <кусок>_ru.json`, translate-python `get_all_data_for_editing` → `original_translations`). Правка куска пишется
 * текстом всей фразы у РОДИТЕЛЯ в мастере (`stream_editor._mutate_master` → `master_body`), а json самого куска в паре
 * до перераскладки фразы — прежний: ответ про кусок (`piece_answer`, который вписал бы в него новый текст) не
 * выполняется — ручка правки отвечает `JSONResponse`, и `_mutate_master` возвращает его раньше. Опрос эфира (раз в
 * 20 с) и перечитывание папки берут строки, которые человек не правит, с сервера — и взяли прежний русский куска.
 *
 * Теперь своё сохранённое держится: после удачного сохранения русского реплики эфира её текст на ленте остаётся
 * сохранённым, пока сервер отдаёт для неё ПРЕЖНИЙ текст (тот, что был у сервера до правки). Сервер прислал другой
 * текст (свою правку догнал или реплику поправил кто-то ещё), реплика ушла (фраза разложена заново, новые id) или
 * прошло `ttlMs` (5 мин: круг правки с ожиданием прохода папки длится минуты) — держать нечего, на экране сервер.
 * Чужие и машинные правки не держатся: только то, что сохранил этот редактор.
 */

// Папки и реплики — в том виде, в каком их отдаёт сервер (как и прежде, без строгого типа).
type Row = any;
type Folder = any;

export interface SavedSourcePins {
    /**
     * Данные папок языка легли с сервера: запомнить русский сервера по репликам и вернуть папки, где у держащихся
     * реплик — сохранённый текст. Неизменённые папки и реплики — те же объекты.
     */
    land: (lang: string, folders: Folder[]) => Folder[];
    /** Русский реплики сохранён (сервер ответил без отказа): держать его, пока сервер не догонит. */
    saved: (lang: string, folderNum: number, id: string, text: string) => void;
    /** Сколько реплик держится сейчас (для тестов и отладки). */
    size: () => number;
}

export const SAVED_SOURCE_PIN_TTL_MS = 5 * 60 * 1000;

const norm = (text: unknown): string => String(text ?? "").replace(/\s+/g, " ").trim();
const rowId = (row: Row): string => String(row?.id ?? row?.original_id ?? "");
const keyOf = (lang: string, folderNum: number, id: string) => `${lang}:${folderNum}:${id}`;

interface Pin {
    lang: string;
    folderNum: number;
    id: string;
    text: string;
    before: string;
    until: number;
}

export function createSavedSourcePins(
    opts: { now?: () => number; ttlMs?: number } = {},
): SavedSourcePins {
    const now = opts.now || (() => Date.now());
    const ttlMs = opts.ttlMs ?? SAVED_SOURCE_PIN_TTL_MS;
    // Русский сервера по репликам — каким он был в последних данных с сервера (до держания).
    const server = new Map<string, string>();
    const pins = new Map<string, Pin>();

    return {
        land: (lang, folders) => {
            if (!Array.isArray(folders)) return folders;
            const t = now();
            const out = folders.map((folder: Folder, index: number) => {
                const folderNum = index + 1;
                const rows: Row[] = Array.isArray(folder?.translations) ? folder.translations : null;
                if (!rows) return folder;
                const seen = new Set<string>();
                let changed = false;
                const next = rows.map((row: Row) => {
                    const id = rowId(row);
                    if (!id) return row;
                    const key = keyOf(lang, folderNum, id);
                    seen.add(key);
                    server.set(key, norm(row.captions));
                    const pin = pins.get(key);
                    if (!pin) return row;
                    const there = norm(row.captions);
                    if (t > pin.until || there !== pin.before) {
                        // Сервер догнал (или реплику переписал кто-то ещё), или держать уже поздно.
                        pins.delete(key);
                        return row;
                    }
                    changed = true;
                    return { ...row, captions: pin.text };
                });
                // Держащаяся реплика, которой сервер в этой папке уже не прислал (фраза разложена заново), — уходит.
                pins.forEach((pin, key) => {
                    if (pin.lang === lang && pin.folderNum === folderNum && !seen.has(key)) pins.delete(key);
                });
                return changed ? { ...folder, translations: next } : folder;
            });
            return out;
        },
        saved: (lang, folderNum, id, text) => {
            const key = keyOf(lang, folderNum, String(id));
            const before = server.get(key);
            if (before === undefined || before === norm(text)) {
                // Сервер реплики не присылал (её нет на сервере) или у него уже этот текст — держать нечего.
                pins.delete(key);
                return;
            }
            pins.set(key, { lang, folderNum, id: String(id), text, before, until: now() + ttlMs });
        },
        size: () => pins.size,
    };
}
